import * as React from 'react'
import { PreviewCard as PreviewCardPrimitive } from '@base-ui/react/preview-card'
import { cn } from '@/shared/lib/utils'

const HoverCardDelayContext = React.createContext<{
  delay: number
  closeDelay: number
}>({
  delay: 200,
  closeDelay: 100,
})

type HoverCardProps = PreviewCardPrimitive.Root.Props & {
  /** Radix-compat open delay (ms); applied to triggers unless overridden. */
  openDelay?: number
  /** Radix-compat close delay (ms); applied to triggers unless overridden. */
  closeDelay?: number
}

function HoverCard({
  openDelay = 200,
  closeDelay = 100,
  ...props
}: HoverCardProps) {
  return (
    <HoverCardDelayContext value={{ delay: openDelay, closeDelay }}>
      <PreviewCardPrimitive.Root data-slot='hover-card' {...props} />
    </HoverCardDelayContext>
  )
}

function HoverCardTrigger({
  asChild = false,
  render,
  children,
  delay,
  closeDelay,
  ...props
}: PreviewCardPrimitive.Trigger.Props & {
  asChild?: boolean
}) {
  const defaults = React.useContext(HoverCardDelayContext)
  let resolvedRender = render
  let resolvedChildren = children

  if (asChild) {
    const child = React.Children.only(children) as React.ReactElement<{
      children?: React.ReactNode
    }>
    resolvedRender = child
    resolvedChildren = child.props.children
  }

  return (
    <PreviewCardPrimitive.Trigger
      data-slot='hover-card-trigger'
      delay={delay ?? defaults.delay}
      closeDelay={closeDelay ?? defaults.closeDelay}
      render={resolvedRender}
      {...props}
    >
      {resolvedChildren}
    </PreviewCardPrimitive.Trigger>
  )
}

function HoverCardContent({
  className,
  align = 'start',
  alignOffset = 0,
  side = 'bottom',
  sideOffset = 4,
  children,
  ...props
}: PreviewCardPrimitive.Popup.Props &
  Pick<
    PreviewCardPrimitive.Positioner.Props,
    'align' | 'alignOffset' | 'side' | 'sideOffset'
  >) {
  return (
    <PreviewCardPrimitive.Portal>
      <PreviewCardPrimitive.Positioner
        align={align}
        alignOffset={alignOffset}
        side={side}
        sideOffset={sideOffset}
        className='isolate z-50'
      >
        <PreviewCardPrimitive.Popup
          data-slot='hover-card-content'
          className={cn(
            'z-50 max-w-sm origin-(--transform-origin) rounded-md border bg-popover p-3 text-sm text-popover-foreground shadow-md outline-hidden data-[side=bottom]:slide-in-from-top-2 data-[side=inline-end]:slide-in-from-left-2 data-[side=inline-start]:slide-in-from-right-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 data-open:animate-in data-open:fade-in-0 data-open:zoom-in-95 data-closed:animate-out data-closed:fade-out-0 data-closed:zoom-out-95',
            className
          )}
          {...props}
        >
          {children}
        </PreviewCardPrimitive.Popup>
      </PreviewCardPrimitive.Positioner>
    </PreviewCardPrimitive.Portal>
  )
}

export { HoverCard, HoverCardTrigger, HoverCardContent }
